import { Box, styled } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import DVHLineChart from '~/components/Charts/DVHLineChart';
import SwapToFullScreen from '~/containers/viewer/viewport/ViewportOverlay/SwapToFullScreen';
import useDose from '~/hooks/patient/dose/useDose';
import {
    roiColorsState,
    roisVisibilityState,
} from '~/state/atoms/structureSet/roiAtoms';
import { showDVHViewComponentFullScreenState } from '~/state/atoms/viewerAtoms';

const ChartOverlayWrap = styled(Box)`
    width: 100%;
    height: 100%;
    position: relative;
    border: 1px solid ${({ theme }) => theme.palette.primary.light};
    border-radius: ${({ theme }) => theme.shape.borderRadius}px;
`;

const ChartOverlaySectionWrap = styled(Box)`
    position: absolute;
`;

const DVHLineChartContainer = styled(Box)`
    height: 95%;
    width: 99%;
`;

const DVHLineChartComponent = () => {
    const { dose } = useDose();
    const [showDVHViewComponentFullScreen, setShowDVHViewComponentFullScreen] =
        useRecoilState(showDVHViewComponentFullScreenState);

    const containerRef = useRef<HTMLDivElement>(null);
    const [dvhResolution, setDVHResolution] = useState(100);
    const updateDVHResolution = () => {
        if (containerRef.current) {
            setDVHResolution(containerRef.current.offsetWidth);
        }
    };

    useEffect(() => {
        updateDVHResolution();
        const resizeObserver = new ResizeObserver(updateDVHResolution);
        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }
        return () => {
            resizeObserver.disconnect();
        };
    }, [containerRef.current]);

    const roiNumbers = useMemo(
        () => dose?.dvhs.map((dvh) => dvh.roiNumber) ?? [],
        [dose]
    );
    const roiVisibilityMap = useRecoilValue(roisVisibilityState(roiNumbers));
    const roiColorMap = useRecoilValue(roiColorsState(roiNumbers));

    const dvhData = useMemo(() => {
        if (!dose) return [];
        return dose.dvhs
            .map((dvh) => ({
                ...dvh,
                color: roiColorMap.get(dvh.roiNumber) ?? dvh.color,
            }))
            .filter((dvh) => roiVisibilityMap.get(dvh.roiNumber) || false);
    }, [dose, roiVisibilityMap, roiColorMap]);

    return (
        <ChartOverlayWrap>
            <ChartOverlaySectionWrap right={15} top={15} sx={{ zIndex: 999 }}>
                <SwapToFullScreen
                    showFullscreen={showDVHViewComponentFullScreen}
                    setShowFullscreen={setShowDVHViewComponentFullScreen}
                />
            </ChartOverlaySectionWrap>
            {dose && (
                <DVHLineChartContainer
                    ref={containerRef}
                    data-testid="dvh-line-chart-container"
                >
                    <DVHLineChart
                        key={
                            showDVHViewComponentFullScreen
                                ? 'fullscreen'
                                : 'normal'
                        }
                        data={dvhData}
                        normalizationValueGy={dose.normalizationValueGy}
                        resolution={dvhResolution}
                    />
                </DVHLineChartContainer>
            )}
        </ChartOverlayWrap>
    );
};

export default DVHLineChartComponent;
